import { Component, EventEmitter, Input, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DefaultConst, Order } from 'src/app/core';

import {
  Column,
  GenericTableConfiguration,
} from './generic-table.configuration';

@Component({
  selector: 'app-generic-table',
  templateUrl: './generic-table.component.html',
  styleUrls: ['./generic-table.component.less'],
})
export class GenericTableComponent {
  Number = Number;
  selectedDataList: any[] = [];
  checked = false;
  indeterminate = false;
  Array = Array;
  expandSet = new Set<number>();
  private _data: any[] = [];
  private _configuration: GenericTableConfiguration;

  @Input() isLoading = false;
  @Input()
  set data(val: any[]) {
    this.expandSet.clear();

    if (val) {
      this._data = val;
      this.selectedDataList = val.filter((item) => item.isChecked);
      if (this.selectedDataList.length) {
        this.genericTableItemCheck.emit(this.selectedDataList);
      }
      this.refreshCheckedStatus();
    }
  }

  get data(): any[] {
    return this._data;
  }

  @Input()
  set configuration(config: GenericTableConfiguration) {
    this._configuration = this.translateConfiguration(config);
  }

  get configuration(): GenericTableConfiguration {
    return this._configuration;
  }

  @Output()
  genericTableActionClick: EventEmitter<{ action: string; value: any }> =
    new EventEmitter();

  @Output()
  genericTableItemCheck: EventEmitter<any[]> = new EventEmitter();

  @Output()
  genericTableSortOrderChange: EventEmitter<{
    sortField: string;
    sortOrder: Order;
  }> = new EventEmitter();

  @Output()
  genericTableFilterSubmit: EventEmitter<{
    property: string;
    filterFields: string[];
  }> = new EventEmitter();

  @Output()
  genericTableCustomFilterSubmit: EventEmitter<{
    column: Column;
    value: string;
  }> = new EventEmitter();

  @Output() cellInputValueChange: EventEmitter<{
    data: any;
    propertyName: string;
    value: any;
  }> = new EventEmitter<{ data: any; propertyName: string; value: any }>();

  constructor(private translateService: TranslateService) {}

  getStrPath(path) {
    return `${this.configuration?.translationBasePath}.${path}`;
  }

  onActionClick(action: string, value: any) {
    for (const item of this.data) {
      item.isChecked = item === value;
    }
    this.selectedDataList = [];
    this.selectedDataList.push(value);
    this.genericTableItemCheck.emit(this.selectedDataList);
    this.genericTableActionClick.emit({ action, value });
    this.refreshCheckedStatus();
  }

  onItemCheck(data: any, isChecked: boolean) {
    data.isChecked = isChecked;
    if (isChecked) {
      this.selectedDataList.push(data);
    } else {
      this.selectedDataList = this.selectedDataList.filter(
        (item) => item !== data
      );
    }
    this.refreshCheckedStatus();
    this.genericTableItemCheck.emit(this.selectedDataList);
  }

  onAllChecked(isChecked: boolean): void {
    for (const item of this.data) {
      item.isChecked = isChecked;
    }
    this.selectedDataList = this.data.filter((item) => item.isChecked);
    this.refreshCheckedStatus();
    this.genericTableItemCheck.emit(this.selectedDataList);
  }

  refreshCheckedStatus(): void {
    this.checked = this.data.every((item) => item.isChecked === true);
    this.indeterminate =
      this.data.some((item) => item.isChecked === true) && !this.checked;
  }

  onSortOrderChange(sortField: any, order: 'ascend' | 'descend' | null) {
    (this._configuration.columnConfigurations.columns || []).forEach((col) => {
      col.property !== sortField
        ? (col.sortOrder = null)
        : (col.sortOrder = order);
    });

    let sortOrder: Order | null;
    switch (order) {
      case 'ascend':
        sortOrder = Order.ASC;
        break;

      case 'descend':
        sortOrder = Order.DESC;
        break;

      default:
        sortOrder = null;
        break;
    }
    this.genericTableSortOrderChange.emit({ sortField, sortOrder });
  }

  onFilterSubmit(property: string, filterFields: string[]) {
    this.genericTableFilterSubmit.emit({ property, filterFields });
  }

  onCustomFilterSubmit(column: Column, value: string) {
    this.genericTableCustomFilterSubmit.emit({ column, value });
  }

  onCustomFilterReset(column: Column, inpSearch: any) {
    inpSearch.value = '';
    this.genericTableCustomFilterSubmit.emit({
      column,
      value: DefaultConst.CUSTOM_FILTER_RESET,
    });
  }

  onCellInputValueChange(
    data: any,
    propertyName: string,
    value: any,
    event?: any
  ): void {
    if (!event || (event && !event.target.disabled)) {
      this.onItemCheck(data, isNaN(value) ? value.length > 0 : +value > 0);
      this.cellInputValueChange.emit({ data, propertyName, value });
    }
  }

  onExpandChange(id: number, checked: boolean): void {
    if (checked) {
      this.expandSet.add(id);
    } else {
      this.expandSet.delete(id);
    }
  }

  private translateConfiguration(
    config: GenericTableConfiguration
  ): GenericTableConfiguration {
    if (config && config.columnConfigurations) {
      (config.columnConfigurations.columns || []).forEach((col) => {
        const CUSTOM_FILTER_PLACEHOLDER =
          'GENERAL.GENERIC_TABLE.COLUMNS.CUSTOM_FILTER_PLACEHOLDER';

        // translate custome filter place holder
        if (col.filterSettings) {
          if (
            col.filterSettings.customFilter &&
            !col.filterSettings?.customFilterPlaceHolder
          ) {
            let text;
            text = this.translateService.instant(CUSTOM_FILTER_PLACEHOLDER);
            if (
              col.translation &&
              col.translation.key &&
              config.translationBasePath
            ) {
              text +=
                ' ' +
                this.translateService.instant(
                  `${config.translationBasePath}.${col.translation.key}.TITLE`
                );
            } else {
              text += ' ' + col.property;
            }
            col.filterSettings.customFilterPlaceHolder = text;
          }

          // translate filters
          (col.filterSettings.filters || []).forEach((f) => {
            f.text = this.translateService.instant(f.text);
          });
        }
      });
    }
    return config;
  }
}
